import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { CohortMultiSelect } from "@/components/CohortMultiSelect";
import { Search, RefreshCw, Loader2, Ticket, Printer, ChevronLeft, ChevronRight } from "lucide-react";
import { listRegistrations, checkinRegistration } from "@/lib/api";
import { buildTokenSlipPdf } from "@/lib/registrationCopy";
import { useAdminAuth } from "@/lib/adminAuth";

const PAGE_SIZE = 25;

// Check-in Desk's whole job: find a token-mode candidate on reporting day,
// assign them a token number, print the slip. Deliberately list-only — no
// drill-in to documents/policies, and seat-mode candidates (already have a
// fixed seat, nothing to check in) never show up here.
export default function AdminCheckin() {
  const nav = useNavigate();
  const { session } = useAdminAuth();
  const role = session?.role;
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [cohortIds, setCohortIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (session && role !== "checkin" && role !== "superadmin") {
      nav("/admin", { replace: true });
    }
  }, [session, role, nav]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    const params = {
      page,
      page_size: PAGE_SIZE,
      modes: "token,walkin",
    };
    if (debouncedQ) params.q = debouncedQ;
    if (cohortIds.length) params.cohort_ids = cohortIds.join(",");
    const data = await listRegistrations(params);
    setRows(data.items || []);
    setTotal(data.total || 0);
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortIds, page, debouncedQ]);

  // Backend already filters token/walkin + excludes withdrawn/rejected via modes=.
  const applications = useMemo(() => rows.map((r) => ({
    id: r.application_number,
    applicationNumber: r.application_number,
    name: r.candidate?.name || "—",
    mobile: r.mobile || "",
    venue: r.assigned_slot?.venue || "—",
    dateLabel: r.assigned_slot?.dateLabel || "—",
    timeLabel: r.assigned_slot?.timeLabel || "—",
    tokenNumber: r.token_number,
    slotGroupId: r.slot_group_id,
    checkedInAt: r.checked_in_at,
    raw: r,
  })), [rows]);

  useEffect(() => { setPage(1); }, [q, cohortIds]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paged = applications;

  const printSlip = async (app) => {
    const win = window.open("about:blank", "_blank");
    try {
      toast.loading("Preparing token slip…", { id: "token-slip-pdf" });
      const { url, fileName } = await buildTokenSlipPdf(app);
      if (win) {
        win.location.href = url;
        setTimeout(() => { try { win.focus(); win.print(); } catch (_e) { /* ignore */ } }, 900);
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = fileName; a.click();
      }
      toast.success("Token slip ready", { id: "token-slip-pdf" });
    } catch (_e) {
      try { win && win.close(); } catch (_ignored) { /* noop */ }
      toast.error("Could not generate token slip", { id: "token-slip-pdf" });
    }
  };

  const doCheckin = async (a) => {
    setCheckingIn(a.id);
    const res = await checkinRegistration(a.id, { actor_role: role });
    setCheckingIn(null);
    if (res.ok) {
      setRows((prev) => prev.map((r) => (r.application_number === a.id ? res.data : r)));
      toast.success(`Checked in — token #${res.data.slot_group_id}-${res.data.token_number}`);
      await printSlip(res.data);
    } else {
      toast.error(res.data?.detail || "Could not check in — please try again");
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F7F7F7]">
      <DashboardSidebar variant="admin" />
      <main className="flex-1 p-6 lg:p-10 max-w-6xl w-full">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="tiny-label">Admin Console</div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#111] tracking-tight mt-1">Check-in Desk</h1>
            <p className="text-[#6B7280] mt-1">Search for a token-mode candidate, check them in, and print their token slip.</p>
          </div>
          <button onClick={() => load()} className="mu-btn-secondary text-sm" data-testid="btn-refresh-checkin">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </button>
        </div>

        <div className="mu-card mt-8" data-testid="checkin-table">
          <div className="p-4 md:p-5 border-b border-[#E5E7EB] flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="input-search-checkin"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, application no., mobile or token no."
                className="mu-input pl-9"
              />
            </div>
            <CohortMultiSelect selected={cohortIds} onChange={setCohortIds} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F7F7] text-[#6B7280] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Application No.</th>
                  <th className="text-left px-6 py-3 font-semibold">Candidate</th>
                  <th className="text-left px-6 py-3 font-semibold">Venue / Window</th>
                  <th className="text-left px-6 py-3 font-semibold">Status</th>
                  <th className="text-right px-6 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-16 text-center text-[#9CA3AF]">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading applications…
                    </td>
                  </tr>
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-14 text-center text-[#9CA3AF]">
                      No token-mode applications match your search.
                    </td>
                  </tr>
                ) : (
                  paged.map((a) => (
                    <tr key={a.id} className="hover:bg-[#F7F7F7]/60 transition" data-testid={`checkin-row-${a.id}`}>
                      <td className="px-6 py-3 text-[#111] font-medium">{a.applicationNumber}</td>
                      <td className="px-6 py-3">
                        <div className="text-[#111] font-medium">{a.name}</div>
                        <div className="text-xs text-[#6B7280]">{a.mobile}</div>
                      </td>
                      <td className="px-6 py-3 text-[#6B7280]">
                        <div>{a.venue}</div>
                        <div className="text-xs">{a.dateLabel} · {a.timeLabel}</div>
                      </td>
                      <td className="px-6 py-3">
                        {a.checkedInAt ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-1 rounded-full">
                            <Ticket className="h-3.5 w-3.5" /> Token #{a.slotGroupId}-{a.tokenNumber}
                          </span>
                        ) : (
                          <span className="text-xs text-[#9CA3AF]">Not checked in</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {a.checkedInAt ? (
                          <button
                            onClick={() => printSlip(a.raw)}
                            className="mu-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1"
                            data-testid={`btn-reprint-${a.id}`}
                          >
                            <Printer className="h-3.5 w-3.5" /> Reprint
                          </button>
                        ) : (
                          <button
                            onClick={() => doCheckin(a)}
                            disabled={checkingIn === a.id}
                            className="mu-btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"
                            data-testid={`btn-checkin-${a.id}`}
                          >
                            {checkingIn === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ticket className="h-3.5 w-3.5" />}
                            Check In
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && total > 0 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-[#E5E7EB]">
              <span className="text-xs text-[#6B7280]">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="mu-btn-secondary text-xs px-2 py-1 disabled:opacity-40"
                  data-testid="btn-checkin-prev-page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="text-xs px-1">Page {page} / {pageCount}</span>
                <button
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                  className="mu-btn-secondary text-xs px-2 py-1 disabled:opacity-40"
                  data-testid="btn-checkin-next-page"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
